import React, { useEffect, useRef, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Lightbox — fullscreen black overlay that shows a single artwork.
 *
 * UX:
 *   • Tap the backdrop or the X → close.
 *   • Press Escape → close.
 *   • Tap the image → toggle 1× ↔ 2× zoom.
 *   • Press ← / → → previous / next piece in the collection (wraps around).
 *   • Pinch on touch devices → native browser pinch-zoom.
 */
const Lightbox = ({ piece, onClose, onPrev, onNext }) => {
  const [zoomed, setZoomed] = useState(false);
  const wrapRef = useRef(null);

  // Lock body scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Reset zoom whenever the piece changes (arrow nav)
  useEffect(() => {
    setZoomed(false);
  }, [piece.id]);

  // Keyboard: Escape closes, ←/→ navigate
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft" && onPrev) onPrev();
      else if (e.key === "ArrowRight" && onNext) onNext();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose, onPrev, onNext]);

  const onBackdropMouseDown = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const toggleZoom = (e) => {
    e.stopPropagation();
    setZoomed((z) => !z);
    requestAnimationFrame(() => {
      const w = wrapRef.current;
      if (!w) return;
      w.scrollTo({
        left: (w.scrollWidth - w.clientWidth) / 2,
        top: (w.scrollHeight - w.clientHeight) / 2,
      });
    });
  };

  return (
    <div
      className="lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={`${piece.title} — fullscreen`}
      data-testid="lightbox"
      onMouseDown={onBackdropMouseDown}
    >
      <button
        type="button"
        className="lightbox__close"
        onClick={onClose}
        aria-label="Close fullscreen"
        data-testid="lightbox-close"
      >
        <X size={18} strokeWidth={1.6} />
      </button>

      {onPrev && (
        <button
          type="button"
          className="lightbox__nav lightbox__nav--prev"
          onClick={onPrev}
          aria-label="Previous artwork"
          data-testid="lightbox-prev"
        >
          <ChevronLeft size={22} strokeWidth={1.6} />
        </button>
      )}

      {onNext && (
        <button
          type="button"
          className="lightbox__nav lightbox__nav--next"
          onClick={onNext}
          aria-label="Next artwork"
          data-testid="lightbox-next"
        >
          <ChevronRight size={22} strokeWidth={1.6} />
        </button>
      )}

      <div
        ref={wrapRef}
        className={`lightbox__wrap ${zoomed ? "lightbox__wrap--zoomed" : ""}`}
        onMouseDown={onBackdropMouseDown}
      >
        <img
          className={`lightbox__img ${zoomed ? "lightbox__img--zoomed" : ""}`}
          src={piece.src}
          alt={piece.alt}
          onClick={toggleZoom}
          draggable={false}
          data-testid="lightbox-img"
        />
      </div>

      <div className="lightbox__caption" data-testid="lightbox-caption">
        <p className="lightbox__title">{piece.title}</p>
        {piece.description && (
          <p className="lightbox__desc">{piece.description}</p>
        )}
      </div>
    </div>
  );
};

export default Lightbox;
